import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { browserHistory, Link } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import * as d3 from 'd3';
import RiskFinderComponent from '../RiskFinderComponent';
import styles from './ZillowData.css';
import withStyles from '../../decorators/withStyles';
import { elevMeta, isUS, toFixed } from "../../utils/utils";


class ZillowData extends Component {

  static propTypes = {
    zillowForecastSubtype: PropTypes.string.isRequired,
    zillowModel: PropTypes.string.isRequired,
    zillowPathway: PropTypes.string.isRequired,
    zillowProb: PropTypes.string.isRequired,
  };

  static defaultProps = {
    zillowForecastSubtype: 'RL1',
    zillowModel: 'DP16',
    zillowPathway: 'RCP45',
    zillowProb: 'p50',
  };

  constructor(props) {
    super(props);
    this.state = {
      data: {},
      dlType: "zillow-summary",
      downloadOpen: false,
      settingsOpen: false,
      sourcesOpen: false,
    };
  }

  getComparisonType() {
    const { place } = this.props;
    const comparisons = {
      county: 'all',
      state: 'children',
    };
    return comparisons[place.type] || 'peers';
  }

  calculateRiskGrowthFactor(data) {
    const total = 'total';
    const exposed = 'exposed';
    const safe = 'safe';
    const defaultData = {};

    defaultData[exposed] = 0;
    defaultData[total] = 0;

    const since0000 = data.ZillowCountSince0000 || defaultData;
    const since2010 = data.ZillowCountSince2010 || defaultData;
    const from0000to2009 = {};

    from0000to2009[total] = since0000[total] - since2010[total];
    from0000to2009[exposed] = since0000[exposed] - since2010[exposed];
    from0000to2009[safe] = from0000to2009[total] - from0000to2009[exposed];
    since2010[safe] = since2010[total] - since2010[exposed];

    [exposed, safe].forEach(function(variable) {
      if (since2010[variable] === 0) {
        data[`${variable}GrowthRate`] = 0;
      } else if (from0000to2009[variable] === 0) {
        data[`${variable}GrowthRate`] = Infinity;
      } else {
        data[`${variable}GrowthRate`] = since2010[variable] / from0000to2009[variable];
      }
    });

    if (data[`${exposed}GrowthRate`] === 0 && data[`${safe}GrowthRate`] === 0) {
      data.riskyGrowthFactor = null;
    } else if (data[`${safe}GrowthRate`] === 0) {
      data.riskyGrowthFactor = Infinity;
    } else {
      const riskyGrowthFactor = (data[`${exposed}GrowthRate`] / data[`${safe}GrowthRate`]);
      data.riskyGrowthFactor = Math.round(riskyGrowthFactor * 1e1) / 1e1;
    }

    // save these if you want to double check calculations
    data.ZillowCountSince2010 = since2010;
    data.ZillowCount0000to2009 = from0000to2009;

    return data;
  }

  addRiskGrowthFactor(groupedData) {
    for (let year of ['2050', '2100']) {
      const placeTypes = Object.keys(groupedData[year]);
      for (let placeType of placeTypes) {
        const places = Object.keys(groupedData[year][placeType]);
        for (let place of places) {
          const data = groupedData[year][placeType][place];
          groupedData[year][placeType][place] = this.calculateRiskGrowthFactor(data);
        }
      }
    }
    return groupedData;
  }

  async getData(comparison) {
    const { place, zillowForecastSubtype, zillowModel, zillowProb } = this.props;
    const models = ['DP16', 'BK'];
    const pathways = ['RCP26', 'RCP45', 'RCP85'];
    const urls = [];
    models.forEach((model) => {
      pathways.forEach((pathway) => {
        const scenario = `${model}_${pathway}_${zillowProb}`;
        urls.push(`/api/housing/${comparison}/${place.type}/${place.id}/${zillowForecastSubtype}/${scenario}`);
      });
    });
    const results = await Promise.all(urls.map((url) => d3.json(url)));
    
    const zillowData = {};

    let resultIdx = 0;
    models.forEach((model) => {
      zillowData[model] = {};
      pathways.forEach((pathway) => {
        const pathwayResult = results[resultIdx];
        zillowData[model][pathway] = this.addRiskGrowthFactor(pathwayResult);
        resultIdx++;
      });
    });
    return zillowData;
  }

  async loadData() {
    const { place } = this.props;

    if (!place.id) {
      return;
    }
    const comparison = this.getComparisonType();
    const zillowData = await this.getData(comparison);

    this.setState({data: {zillowData}});
  }

  componentDidMount() {
    this.loadData();
  }

  hasChanged(props) {
    return (props.place.id !== this.props.place.id 
      || props.place.type !== this.props.place.type 
      || props.zillowProb !== this.props.zillowProb);
  }

  componentDidUpdate(props) {
    if (this.hasChanged(props)) {
      this.loadData();
    }
  }

  hasData() {
    return this.state && this.state.data && Object.keys(this.state.data).length > 0;
  }

  noData = (_, value) => {
    return value === null;
  };

  render() {
    if (!this.hasData()) {
      return null;
    }

    return (
      <div className="ZillowData">
        <div>{React.Children.map(this.props.children,
          (child) => React.cloneElement(child, {
            data: this.state.data,
            noData: this.noData,
            elevMeta: elevMeta(this.props.place.id),
            placeID: this.props.place.id,
            placeType: this.props.place.type,
            ...this.props
          }))}
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(styles),
  connect((state) => state),
)(ZillowData);
